// html-editor.component.ts - Signature added only on init

/* ───── SignatureBlot (register once, e.g. main.ts) ───── */
import Quill from 'quill';

// Quill.import() is typed as unknown → cast to any
const BlockEmbed = Quill.import('blots/block/embed') as any;

class SignatureBlot extends BlockEmbed {
  /* static meta — MUST be inside the class for TS */
  static blotName  = 'signature';
  static tagName   = 'div';
  static className = 'email-signature-content';   // Quill auto-adds this class

  static create(value: string) {
    const node = super.create() as HTMLElement;
    node.setAttribute('id', 'signature-wrapper');   // survives sanitiser
    node.setAttribute('data-signature', 'true');
    node.setAttribute('contenteditable', 'false');

    /* inline styles (safe in most email clients) */
    // Use CSS custom property for theme-aware border color
    node.style.borderTop  = '1px solid var(--form-border-color, #e0e0e0)';
    node.style.paddingTop = '10px';
    node.style.marginTop  = '20px';

    node.innerHTML = value;                         // actual signature markup
    return node;
  }

  static value(node: HTMLElement) {
    return node.innerHTML;                          // what getContents() returns
  }
}

/* register once, before any editor is instantiated */
Quill.register({ 'formats/signature': SignatureBlot });


import Delta from 'quill-delta';

import { Component, effect, forwardRef, inject, input, Input, OnDestroy, OnInit, ViewEncapsulation, Output, EventEmitter } from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  EditorChangeContent,
  EditorChangeSelection,
  QuillEditorComponent,
  QuillViewComponent,
  QuillViewHTMLComponent,
} from 'ngx-quill';
import { MaterialModule } from 'src/app/material.module';
import { SpeechService } from './spech-text';
import { MatDialog } from '@angular/material/dialog';
import { LinkDialogComponent } from './link/link-create';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslationService } from 'src/app/services/translante.service';
import { TranslationDialogComponent } from './link/translante';

@Component({
  selector: 'app-html-editor',
  standalone: true,
  imports: [
    QuillEditorComponent,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HtmlEditorComponent),
      multi: true,
    },
  ],
  templateUrl: './html-editor.component.html',
  styleUrl: './html-editor.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class HtmlEditorComponent implements ControlValueAccessor, OnInit, OnDestroy {
  private dialog = inject(MatDialog);
  private translationService = inject(TranslationService);
  private snackBar = inject(MatSnackBar);
  private formValueChangeSub: Subscription;
  
  // Translation state
  private originalContent: string = '';
  private isTranslated: boolean = false;
  @Output() translationStateChanged = new EventEmitter<boolean>();
  
  // Signature configuration
  private readonly SIGNATURE_CONTAINER_CLASS = 'email-signature-container';
  private readonly SIGNATURE_CONTENT_CLASS = 'email-signature-content';
  private readonly SIGNATURE_WRAPPER_ID = 'signature-wrapper';
  
  // Flag to track if signature was added
  private _signatureAdded: boolean = false;
  
  constructor(private speechService: SpeechService) {
    const icons : any = Quill.import('ui/icons');

    // Add speech-to-text icon with SVG
    icons.speechToText = `<svg viewBox="0 0 24 24" width="18" height="18">
      <path fill="currentColor" d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
      <path fill="currentColor" d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
    </svg>`;

    // Add translation icon
    icons.translate = `<svg viewBox="0 0 24 24" width="18" height="18">
      <path fill="currentColor" d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"/>
    </svg>`;
  
    effect(()=>{
      if(this.tagsChanged()){
        this.insertTextAtCursor(this.tagsChanged().newValue);
      }
    })
  }

  isSpeechActive: boolean = false;
  _signature: string = '';
  @Input() placeholder: string = 'Enter text here...';
  @Input() formControl!: FormControl;
  @Input() showTranslate: boolean = false;
  tagsChanged = input<any>()
  @Input() set templateChange(templateChange: any) {
    this.addTemplate(templateChange.newValue);
  }
  @Input() set signature(signatureAdd: any) {
    const previousSignature = this._signature;
    this._signature = signatureAdd;
    
    // Add signature if:
    // 1. We have a signature value
    // 2. It's different from the previous one (or first time)
    // 3. Editor exists
    // 4. No signature exists in content
    if (this.quillEditor && this._signature && (this._signature !== previousSignature || !this._signatureAdded)) {
      setTimeout(() => {
        if (this.quillEditor && !this.hasSignature()) {
          this.addSignature();
        }
      }, 100);
    }
  }
  @Input() readOnly: boolean = false;
  @Input() showNoteBackground: boolean = false;
  @Input() quillModule: any = null;
  @Input() height: string = '200px';
  @Input() costumHeader: boolean = false;
  title = input<string>();
  blurred = false;
  focused = false;
  cursorIndex: number = 0;
  content = ``;
  quillEditor: Quill | null = null;
  recognizedText: string = '';
  isListening: boolean = false;

  ngOnInit() {
    // Build toolbar configuration based on showTranslate input
    const toolbarItems = [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['link'],
      ['clean'],
      ['speechToText'],
    ];

    if (this.showTranslate) {
      toolbarItems.push(['translate']);
    }

    if (!this.quillModule) {
      this.quillModule = {
        toolbar: {
          container: toolbarItems,
          handlers: {
            speechToText: this.startSpeechToText.bind(this),
            link: this.handleLinkDialog.bind(this),
            ...(this.showTranslate ? { translate: this.handleTranslate.bind(this) } : {})
          }
        },
        clipboard: {
          matchVisual: false,
          matchers: [
            ['*', (node: any, delta: any) => this.cleanPastedContent(node, delta)]
          ]
        }
      };
    } else {
      if (this.quillModule?.toolbar) {
        this.quillModule.toolbar.handlers = {
          ...this.quillModule.toolbar.handlers,
          speechToText: this.startSpeechToText.bind(this),
          link: this.handleLinkDialog.bind(this),
          ...(this.showTranslate ? { translate: this.handleTranslate.bind(this) } : {})
        };
      }

      if (!this.quillModule.clipboard) {
        this.quillModule.clipboard = {
          matchVisual: false,
          matchers: [
            ['*', (node: any, delta: any) => this.cleanPastedContent(node, delta)]
          ]
        };
      }
    }

    // Register custom link format
    const Link = Quill.import('formats/link') as any;
    
    class CustomLink extends Link {
      static create(value: string) {
        const node = super.create(value);
        node.setAttribute('data-quill-link', 'true');
        return node;
      }
      static formats(domNode: HTMLElement) {
        const href = domNode.getAttribute('href');
        return href && href.trim() ? href : null; // <— avoid true
      }
    }
    
    Quill.register(CustomLink, true);

    if (this.formControl) {
      this.formValueChangeSub = this.formControl.valueChanges.subscribe(value => {
        if (this.content !== value) {
          this.content = value || '';
          
          if (this.quillEditor) {
            const currentContent = this.quillEditor.root.innerHTML;
            if (currentContent !== this.content) {
              const selection = this.quillEditor.getSelection();
              
              this.quillEditor.root.innerHTML = '';
              this.quillEditor.clipboard.dangerouslyPasteHTML(0, this.content);
              
              if (selection) {
                setTimeout(() => {
                  if (this.quillEditor) {
                    this.quillEditor.setSelection(selection.index, 0);
                  }
                }, 0);
              }
            }
          }
        }
      });
    }
  }

  // Public method to check if content is currently translated
  public getCurrentTranslationState(): boolean {
    return this.isTranslated;
  }

  // Method to reset translation state
  private resetTranslationState(): void {
    if (this.isTranslated) {
      this.originalContent = '';
      this.isTranslated = false;
      this.translationStateChanged.emit(false);
    }
  }

  // Handle translate button click
  handleTranslate(): Promise<{ success: boolean, reverted?: boolean }> {
    return new Promise((resolve) => {
      if (!this.quillEditor) {
        resolve({ success: false });
        return;
      }

      // Get the current selection or full content
      const selection = this.quillEditor.getSelection();
      let textToTranslate = '';
      let isFullContent = false;
      let signatureWasRemoved = false;

      // Check if we should revert to original
      if (this.isTranslated && !selection?.length) {
        // Revert to original content only if no text is selected
        if (this.originalContent) {
          const hadSignature = this.hasSignature();
          
          this.quillEditor.root.innerHTML = '';
          this.quillEditor.clipboard.dangerouslyPasteHTML(0, this.originalContent);
          
          // Re-add signature if it was present before
          if (hadSignature && this._signature) {
            setTimeout(() => {
              if (this.quillEditor && !this.hasSignature()) {
                this.addSignature();
              }
            }, 0);
          }
          
          this.isTranslated = false;
          this.originalContent = '';
          this.translationStateChanged.emit(false);
          this.updateContent();
          this.snackBar.open('Reverted to original language', 'Close', { duration: 2000 });
          resolve({ success: true, reverted: true });
          return;
        }
      }

      if (selection && selection.length > 0) {
        textToTranslate = this.quillEditor.getText(selection.index, selection.length);
      } else {
        // Get full content excluding signature
        const fullContent = this.quillEditor.root.innerHTML;
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = fullContent;
        
        // Remove ALL signature-related elements
        const signatureElements = tempDiv.querySelectorAll(
          `.${this.SIGNATURE_CONTAINER_CLASS}, .${this.SIGNATURE_CONTENT_CLASS}, #${this.SIGNATURE_WRAPPER_ID}, [data-signature="true"]`
        );
        
        signatureElements.forEach(el => {
          el.remove();
          signatureWasRemoved = true;
        });
        
        // Clean up trailing empty paragraphs and BRs
        const paragraphs = Array.from(tempDiv.querySelectorAll('p'));
        for (let i = paragraphs.length - 1; i >= 0; i--) {
          const para = paragraphs[i];
          const text = (para.textContent || '').trim();
          const innerHTML = para.innerHTML.trim();
          
          if (!text && (innerHTML === '' || innerHTML === '<br>')) {
            para.remove();
          } else {
            break;
          }
        }
        
        textToTranslate = tempDiv.innerHTML.trim();
        isFullContent = true;
        
        // Store original content before translation (full content before removing signature)
        if (!this.isTranslated) {
          this.originalContent = fullContent;
        }
      }

      if (!textToTranslate.trim()) {
        this.snackBar.open('No text to translate', 'Close', { duration: 2000 });
        resolve({ success: false });
        return;
      }

      // First detect the language
      this.translationService.detectLanguage(textToTranslate).subscribe({
        next: (detectResult) => {
          // Open translation dialog
          const dialogRef = this.dialog.open(TranslationDialogComponent, {
            width: '400px',
            data: {
              currentText: textToTranslate,
              detectedLanguage: detectResult.detectedLanguage
            }
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result && this.quillEditor) {
              const loadingSnackBar = this.snackBar.open('Translating...', '', { duration: 0 });

              // Perform translation
              this.translationService.translateText(textToTranslate, result.targetLanguage).subscribe({
                next: (translationResult) => {
                  loadingSnackBar.dismiss();

                  if (this.quillEditor) {
                    if (isFullContent) {
                      this.quillEditor.root.innerHTML = '';
                      this.quillEditor.clipboard.dangerouslyPasteHTML(0, translationResult.translatedText);
                      
                      // Mark as translated
                      this.isTranslated = true;
                      this.translationStateChanged.emit(true);
                      
                      // Re-add signature if it was removed
                      if (signatureWasRemoved && this._signature) {
                        setTimeout(() => {
                          if (this.quillEditor && !this.hasSignature()) {
                            this.addSignature();
                          }
                        }, 0);
                      }
                    } else if (selection) {
                      // Replace selected text
                      this.quillEditor.deleteText(selection.index, selection.length);
                      
                      if (textToTranslate.includes('<') && textToTranslate.includes('>')) {
                        this.quillEditor.clipboard.dangerouslyPasteHTML(selection.index, translationResult.translatedText);
                      } else {
                        this.quillEditor.insertText(selection.index, translationResult.translatedText);
                      }
                    }

                    this.updateContent();
                    
                    const sourceLanguage = translationResult.sourceLanguage || detectResult.detectedLanguage || 'auto';
                    const targetLanguage = translationResult.targetLanguage || result.targetLanguage || 'unknown';
                    
                    this.snackBar.open(
                      `Translated from ${this.getLanguageName(sourceLanguage)} to ${this.getLanguageName(targetLanguage)}`,
                      'Close',
                      { duration: 3000 }
                    );
                    
                    resolve({ success: true });
                  }
                },
                error: (error) => {
                  loadingSnackBar.dismiss();
                  console.error('Translation error:', error);
                  this.snackBar.open('Translation failed: ' + error.message, 'Close', { duration: 4000 });
                  resolve({ success: false });
                }
              });
            } else {
              resolve({ success: false });
            }
          });
        },
        error: (error) => {
          console.error('Language detection error:', error);
          this.snackBar.open('Language detection failed', 'Close', { duration: 3000 });
          resolve({ success: false });
        }
      });
    });
  }

  getLanguageName(code: string | undefined | null): string {
    if (!code) {
      return 'Unknown';
    }
    
    const languages: { [key: string]: string } = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'pt': 'Portuguese',
      'zh': 'Chinese',
      'ja': 'Japanese',
      'ko': 'Korean',
      'ar': 'Arabic',
      'hi': 'Hindi',
      'ru': 'Russian'
    };
    return languages[code] || code.toUpperCase();
  }

  private cleanPastedContent(node: any, delta: any) {
    if (delta.ops) {
      /* Drop any image or video embeds */
      delta.ops = delta.ops.filter((op: any) =>
        !(op.insert && typeof op.insert === 'object' &&
          ('image' in op.insert || 'video' in op.insert))
      );
  
      /* Keep only whitelisted inline attrs */
      delta.ops = delta.ops.map((op: any) => {
        if (op.attributes) {
          delete op.attributes.background;
          delete op.attributes['background-color'];
          delete op.attributes.font;
          delete op.attributes.size;
          delete op.attributes.color;
  
          const allowed = [
            'bold', 'italic', 'underline', 'strike',
            'link', 'list', 'script', 'indent',
            'direction', 'align', 'header'
          ];
          const keep: any = {};
          allowed.forEach(a => {
            if (op.attributes[a] !== undefined) keep[a] = op.attributes[a];
          });
          op.attributes = Object.keys(keep).length ? keep : undefined;
        }
        return op;
      });
    }
    return delta;
  }
  
  
  created(event: Quill) {
    this.quillEditor = event;
    this.quillEditor.focus();
  
    if (this.content) {
      this.quillEditor.clipboard.dangerouslyPasteHTML(this.content);
    }

    const LinkBlot = Quill.import('formats/link') as any;
    LinkBlot.sanitize = (url: string) => {
      let protocol = url.startsWith('http') ? null : 'https:';
      return protocol ? `${protocol}//${url}` : url;
    };
    Quill.register(LinkBlot, true);
  
    /* 1️⃣  Block image/video tags converted inside HTML */
    this.quillEditor.clipboard.addMatcher('IMG', () => {
      return new Delta();
    });
    this.quillEditor.clipboard.addMatcher('VIDEO', () => {
      return new Delta();
    });

    /* 2️⃣  Stop the native paste in the *capture* phase */
    const pasteGuard = (e: ClipboardEvent) => {
      const cd = e.clipboardData;
      if (!cd) return;

      const hasBinary = Array.from(cd.items)
        .some(i => i.kind === 'file' && /(image|video)\//.test(i.type));

      const html = cd.getData('text/html');
      const htmlHasMedia = /<(img|video)\b/i.test(html);

      if (hasBinary || htmlHasMedia) {
        e.stopPropagation();          // ⬅️  important
        e.preventDefault();
        this.snackBar.open('Pasting images or videos is not allowed',
                           'Close', { duration: 2000 });
      }
    };
    this.quillEditor.root.addEventListener('paste', pasteGuard, true); // capture=true
    

    this.quillEditor.root.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      
      if (target.tagName === 'A' && !this.readOnly) {
        e.preventDefault();
        
        const href = target.getAttribute('href');
        const text = target.textContent || '';
        
        // Try to find where this link actually starts in the document
        const editorText = this.quillEditor.getText();
        const linkStartIndex = editorText.indexOf(text);
        
        if (linkStartIndex !== -1) {
          this.quillEditor.setSelection(linkStartIndex, text.length);
          
          this.handleLinkDialog(true, { 
            index: linkStartIndex, 
            length: text.length,
            href: href, 
            text: text 
          });
        }
        
        return;
      }
      
      if (target.tagName === 'A' && this.readOnly) {
        const href = target.getAttribute('href');
        if (href) {
          window.open(href, '_blank');
        }
      }
    });
    
    // Add text-change listener to track content changes and reset translation state
    this.quillEditor.on('text-change', (delta, oldDelta, source) => {
      if (source === 'user') {
        // User made a change, reset translation state
        this.resetTranslationState();
      }
    });
  
    setTimeout(() => {
      if (!this.quillEditor) {
        return;
      }

      // Check if content already has signature
      if (this.hasSignature()) {
        this._signatureAdded = true;
      } else if (this._signature && !this._signatureAdded) {
        // Add signature only if we have one and haven't added it yet
        this.addSignature();
      }
      
      if (this.quillEditor) {
        const contentLength = this.quillEditor.getLength();
        this.cursorIndex = 0;
        this.quillEditor.setSelection(0);
      }
    }, 50);
  }

  private removeBackgroundStyles(element: HTMLElement): void {
    const allElements = element.querySelectorAll('*');
    const elementsArray = [element, ...Array.from(allElements)];
    
    elementsArray.forEach(el => {
      const htmlEl = el as HTMLElement;
      
      const style = htmlEl.getAttribute('style');
      if (style) {
        const newStyle = style
          .split(';')
          .filter(s => {
            const prop = s.trim().toLowerCase();
            return !prop.startsWith('background') && 
                   !prop.includes('mso-') &&
                   !prop.includes('font-family') &&
                   !prop.includes('color');
          })
          .join(';');
        
        if (newStyle.trim()) {
          htmlEl.setAttribute('style', newStyle);
        } else {
          htmlEl.removeAttribute('style');
        }
      }
      
      htmlEl.removeAttribute('bgcolor');
      htmlEl.removeAttribute('background');
      htmlEl.removeAttribute('color');
    });
  }

  private removeProblematicElements(element: HTMLElement): void {
    const styleTags = element.querySelectorAll('style');
    styleTags.forEach(tag => tag.remove());
    
    const metaTags = element.querySelectorAll('meta');
    metaTags.forEach(tag => tag.remove());
    
    const linkTags = element.querySelectorAll('link');
    linkTags.forEach(tag => tag.remove());
    
    const walker = document.createTreeWalker(
      element,
      NodeFilter.SHOW_COMMENT,
      null
    );
    const comments = [];
    let node;
    while (node = walker.nextNode()) {
      comments.push(node);
    }
    comments.forEach(comment => comment.remove());
  }

  ngOnDestroy(): void {
    if (this.quillEditor) {
      try {
        this.quillEditor.off('text-change', this.onEditorContentChange);
        this.quillEditor.root.innerHTML = '';
        this.quillEditor = null;
      } catch (e) {
        console.warn('Failed to destroy Quill editor:', e);
      }
    }
  
    if (this.formValueChangeSub) {
      this.formValueChangeSub.unsubscribe();
    }
  
    if (this.isSpeechActive) {
      this.speechService.stopRecognition();
    }
  }

  startSpeechToText(event: any) {
    this.isSpeechActive = !this.isSpeechActive;

    const toolbarButton = document.querySelector('.ql-speechToText');
    if (toolbarButton) {
      if (this.isSpeechActive) {
        // Active state - red/recording
        toolbarButton.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" class="speech-active">
          <path fill="currentColor" d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
          <path fill="currentColor" d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
        </svg>`;
        toolbarButton.classList.add('speech-recording');
      } else {
        // Inactive state - normal color
        toolbarButton.innerHTML = `<svg viewBox="0 0 24 24" width="18" height="18" class="speech-inactive">
          <path fill="currentColor" d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
          <path fill="currentColor" d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
        </svg>`;
        toolbarButton.classList.remove('speech-recording');
      }
    }
  
    if (this.isSpeechActive) {
      this.speechService.startRecognition(
        (recognizedText: string) => {
          this.recognizedText = recognizedText;
          this.insertTextAtCursor(recognizedText + ' ');
        },
        (error: Event) => {
          console.error('Speech recognition error:', error);
          this.isSpeechActive = false;
        }
      );
    } else {
      this.speechService.stopRecognition();
    }
  }
  
  private hasSignature(): boolean {
    return !!this.quillEditor?.root.querySelector('[data-signature="true"]');
  }

  addSignature(): void {
    if (!this.quillEditor || !this._signature) { return; }
  
    /* ─── 1. trim trailing empties ─── */
    let len = this.quillEditor.getLength();
    while (len > 1 && !this.quillEditor.getText(len - 2, 1).trim()) {
      this.quillEditor.deleteText(len - 2, 1, 'silent');
      len--;
    }
  
    /* ─── 2. exactly one blank line *before* the signature ─── */
    this.quillEditor.insertText(this.quillEditor.getLength() - 1, '\n', 'silent');
  
    /* ─── 3. embed the signature ─── */
    const pos = this.quillEditor.getLength() - 1;
    this.quillEditor.insertEmbed(pos, 'signature', this._signature, 'silent');
  
    /* 🚫 NO extra “\n” after the embed – Quill already gives you one */
    this._signatureAdded = true;
  }

  findLinkPosition(linkElement: HTMLElement): number {
    if (!this.quillEditor) return -1;
    
    const editorRoot = this.quillEditor.root;
    let currentNode: any = editorRoot.firstChild;
    let index = 0;
    
    while (currentNode && currentNode !== linkElement) {
      if (currentNode.nodeType === Node.TEXT_NODE) {
        index += currentNode.length;
      } else if (currentNode.nodeType === Node.ELEMENT_NODE) {
        if (!currentNode.contains(linkElement)) {
          index += currentNode.textContent ? currentNode.textContent.length : 0;
        } else {
          let child = currentNode.firstChild;
          while (child && child !== linkElement) {
            index += child.textContent ? child.textContent.length : 0;
            child = child.nextSibling;
          }
          return index;
        }
      }
      currentNode = currentNode.nextSibling;
    }
    
    return index;
  }

  updateContent(): void {
    if (!this.quillEditor) return;
    
    const newContent = this.quillEditor.root.innerHTML;
    this.content = newContent;
    this.onChange(newContent);
    
    if (this.formControl) {
      this.formControl.markAsTouched();
      this.formControl.updateValueAndValidity();
    }
  }

  handleLinkDialog(value: boolean, existingLink: any = null) {
    const range = existingLink || this.quillEditor?.getSelection();
    if (!range) return;
    
    const originalPosition = range.index;
    const originalLength = range.length || 0;
    const isExistingLink = !!existingLink;
    
    let dialogData = {
      url: '',
      displayText: '',
      isRemoving: false,
      isEditing: false,
      showDeleteOption: false
    };
    
    if (existingLink && existingLink.href) {
      dialogData = {
        url: existingLink.href,
        displayText: existingLink.text,
        isRemoving: false,
        isEditing: true,
        showDeleteOption: true
      };
    } 
    else if (range.length > 0) {
      const formats: any = this.quillEditor?.getFormat(range) || {};
      dialogData = {
        url: formats.link || '',
        displayText: this.quillEditor?.getText(range.index, range.length) || '',
        isRemoving: false,
        isEditing: !!formats.link,
        showDeleteOption: !!formats.link
      };
    }
  
    const dialogRef = this.dialog.open(LinkDialogComponent, {
      width: '400px',
      data: dialogData
    });
  
    dialogRef.afterClosed().subscribe(result => {
      if (result && this.quillEditor) {
        if (result.removeLink) {
          // If user chose "remove link and text"
          if (result.removeText) {
            this.quillEditor.deleteText(range.index, range.length || 1, 'user');
            this.updateContent();
            this.restoreCursorPosition(range.index, 0);
            return;
          }
        
          // "remove link only (keep text)"
          let target = { index: range.index, length: range.length };
        
          // If selection is collapsed or we came from click-on-link (existingLink)
          // ensure we unwrap the full anchor text
          if (!target.length) {
            const bounds = this.getLinkBounds(range.index);
            if (bounds) target = bounds;
          }
        
          // Final guard: if still no length, fall back to one char (best effort)
          if (!target.length) {
            target.length = 1;
          }
        
          this.quillEditor.formatText(target.index, target.length, 'link', false, 'user');
        
          // Optional: strip any leftover <a> wrapper produced by custom paste/DOM (rare)
          // Re-apply the exact same text to normalize the DOM
          const txt = this.quillEditor.getText(target.index, target.length);
          this.quillEditor.deleteText(target.index, target.length, 'silent');
          this.quillEditor.insertText(target.index, txt, 'silent');
        
          this.updateContent();
          this.restoreCursorPosition(target.index + target.length, 0);
          return;
        }if (result.removeLink) {
          // If user chose "remove link and text"
          if (result.removeText) {
            this.quillEditor.deleteText(range.index, range.length || 1, 'user');
            this.updateContent();
            this.restoreCursorPosition(range.index, 0);
            return;
          }
        
          // "remove link only (keep text)"
          let target = { index: range.index, length: range.length };
        
          // If selection is collapsed or we came from click-on-link (existingLink)
          // ensure we unwrap the full anchor text
          if (!target.length) {
            const bounds = this.getLinkBounds(range.index);
            if (bounds) target = bounds;
          }
        
          // Final guard: if still no length, fall back to one char (best effort)
          if (!target.length) {
            target.length = 1;
          }
        
          this.quillEditor.formatText(target.index, target.length, 'link', false, 'user');
        
          // Optional: strip any leftover <a> wrapper produced by custom paste/DOM (rare)
          // Re-apply the exact same text to normalize the DOM
          const txt = this.quillEditor.getText(target.index, target.length);
          this.quillEditor.deleteText(target.index, target.length, 'silent');
          this.quillEditor.insertText(target.index, txt, 'silent');
        
          this.updateContent();
          this.restoreCursorPosition(target.index + target.length, 0);
          return;
        }
  
        const { url, displayText } = result;
        const currentIndex = range.index;
        let newCursorIndex = currentIndex;
        let newLength = 0;
        
        if (range.length > 0) {
          if (displayText !== this.quillEditor.getText(range.index, range.length)) {
            this.quillEditor.deleteText(range.index, range.length);
            this.quillEditor.insertText(range.index, displayText);
            this.quillEditor.setSelection(range.index, displayText.length);
          }
          this.quillEditor.formatText(range.index, displayText.length, 'link', url);
          newCursorIndex = range.index + displayText.length;
          newLength = 0;
        } 
        else {
          this.quillEditor.insertText(currentIndex, displayText);
          this.quillEditor.setSelection(currentIndex, displayText.length);
          this.quillEditor.formatText(currentIndex, displayText.length, 'link', url);
          newCursorIndex = currentIndex + displayText.length;
          newLength = 0;
        }
        
        const tooltip = document.querySelector('.ql-tooltip');
        if (tooltip) {
          (tooltip as HTMLElement).style.display = 'none';
        }
        
        this.updateContent();
        
        this.restoreCursorPosition(newCursorIndex, newLength);
      } else {
        if (isExistingLink) {
          this.restoreCursorPosition(originalPosition, originalLength);
        } else {
          this.restoreCursorPosition(originalPosition, 0);
        }
      }
    });
  }
  
  private restoreCursorPosition(index: number, length: number = 0): void {
    if (!this.quillEditor) return;
    
    this.cursorIndex = index;
    
    setTimeout(() => {
      if (this.quillEditor) {
        this.quillEditor.setSelection(index, length);
        this.quillEditor.focus();
      }
    }, 10);
  }

  changedEditor(event: EditorChangeContent | EditorChangeSelection | any) {
    if (event.event === 'selection-change' && event.range) {
      this.cursorIndex = event.range.index;
    }
  }

  addTemplate(templateChange: string) {
    if (this.quillEditor) {
      this.quillEditor.setContents([]);
  
      this.quillEditor.clipboard.dangerouslyPasteHTML(0, templateChange);
  
      this.content = templateChange;
  
      this.onChange(templateChange);
      
      // Reset translation state when template changes
      this.resetTranslationState();
      
      // Reset signature flag when template changes
      this._signatureAdded = false;
  
      setTimeout(() => {
        if (!this.quillEditor) return;
        
        // Add signature only if we have one and it's not already in the content
        if (this._signature && !this.hasSignature()) {
          this.addSignature();
        }
  
        const contentLength = this.quillEditor.getLength() - (this._signature ? this._signature.length + 3 : 1);
        this.cursorIndex = contentLength;
        this.quillEditor.setSelection(this.cursorIndex);
      }, 0);
    }
  }

  /** Expand around index to cover the full run that has the same 'link' format */
private getLinkBounds(index: number): { index: number; length: number } | null {
  if (!this.quillEditor) return null;

  // If not inside a link, bail
  const fmtHere: any = this.quillEditor.getFormat(index, 1);
  const url = fmtHere?.link;
  if (!url) return null;

  // Walk left while 'link' matches
  let start = index;
  while (start > 0) {
    const leftFmt: any = this.quillEditor.getFormat(start - 1, 1);
    if (leftFmt?.link !== url) break;
    start -= 1;
  }

  // Walk right while 'link' matches
  let end = index;
  const docLen = this.quillEditor.getLength();
  while (end < docLen - 1) {
    const rightFmt: any = this.quillEditor.getFormat(end, 1);
    if (rightFmt?.link !== url) break;
    end += 1;
  }

  const length = Math.max(0, end - start);
  return length > 0 ? { index: start, length } : null;
}

  focus(event: any) {
    this.focused = true;
    this.blurred = false;
    if (this.quillEditor && this.cursorIndex != null) {
      this.quillEditor.setSelection(this.cursorIndex);
    }
  }
  
  nativeFocus($event: any) {
    // Handle native focus event if needed
  }

  insertTextAtCursor(value: string) {
    if (this.quillEditor && this.cursorIndex !== null) {
      const range = this.quillEditor.getSelection();
      const insertIndex = range ? range.index : this.cursorIndex;
  
      this.quillEditor.insertText(insertIndex, value + "\u00A0");
  
      this.cursorIndex = insertIndex + value.length + 1;
      const newContent = this.quillEditor.root.innerHTML;
      this.content = newContent;
      this.onChange(newContent);
      
      // Reset translation state when text is inserted
      this.resetTranslationState();

      this.quillEditor.focus();
      setTimeout(() => {
        if (this.quillEditor) {
          this.quillEditor.setSelection(insertIndex + value.length + 1);
        }
      }, 0);
    }
  }

  blur(event: any) {
    this.focused = false;
    this.blurred = true;
  }

  nativeBlur($event: any) {
    // Handle native blur event if needed
  }

  onChange = (value: any) => {};
  onTouched = () => {};

  writeValue(value: any): void {
    // Check if the incoming value already has a signature
    if (value && this._signature) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = value;
      
      const hasSignature = tempDiv.querySelector('[data-signature="true"]');
      
      if (hasSignature) {
        this._signatureAdded = true;
      } else {
        // Reset the flag so signature can be added on next init
        this._signatureAdded = false;
      }
    } else {
      this._signatureAdded = false;
    }
    
    this.content = value || '';
    
    // Reset translation state when new value is written
    this.resetTranslationState();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.readOnly = isDisabled;
  }

  onEditorContentChange({ text }: any) {
    if (this.quillEditor) {
      this.onChange(this.content);
    }
  }
}